import React, { useState, useCallback, useEffect } from 'react';
import { MdArrowBack, MdArrowForward } from 'react-icons/md';
import { useFormContext } from '../../context/FormContext';
import { useReadOnly } from '../../hooks/useReadOnly';
import { SignupLayout } from '../ui/SignupLayout';
import { updateQuote } from '../services/QuoteService';
import GoogleAutoComplete from '../ui/GoogleAutoComplete';

// -- Constants
const PARTY_TYPES = [
    { value: 'MORTGAGEE', label: 'Mortgagee' },
    { value: 'LOSS_PAYEE', label: 'Loss Payee' },
    { value: 'ADDITIONAL_INSURED', label: 'Additional Insured' },
];

const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
    'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
    'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
    'VT','VA','WA','WV','WI','WY'
];

const COUNTRIES = ['USA'];

export function AdditionalParty({ history }) {
    const { formData, setField, setFields, setStep } = useFormContext();
    const { isReadOnly, readOnlyProps } = useReadOnly();
    const [errors, setErrors] = useState({});

    useEffect(() => {
        setStep('additionalParty');
    }, []);

    // -- Google Autocomplete for additional party address
    const handleAddress1Change = useCallback((value) => {
        setField('AdditionalPartyAddressOne', value);
    }, [setField]);

    const handlePlaceSelected = useCallback((place) => {
        if (!place?.address_components) return;
        const comp = place.address_components;
        const get = (type) => comp.find((c) => c.types.includes(type)) || {};

        const streetNumber = get('street_number').long_name || '';
        const route = get('route').long_name || '';
        const city = get('locality').long_name || get('sublocality').long_name || '';
        const state = get('administrative_area_level_1').short_name || '';
        const zip = get('postal_code').long_name || '';
        const sub = get('subpremise').long_name || '';

        setFields({
            AdditionalPartyAddressOne: `${streetNumber} ${route}`.trim(),
            AdditionalPartyAddressTwo: sub,
            AdditionalPartyCity: city,
            AdditionalPartyState: state,
            AdditionalPartyZipCode: zip,
            AdditionalPartyCountry: 'USA',
        });
    }, [setFields]);

    // -- Validation
    const validate = () => {
        const e = {};
        if (!formData.AdditionalPartyType)
            e.AdditionalPartyType = 'Please select a party type.';
        if (formData.AdditionalPartyType !== "MORTGAGEE" && !formData.AdditionalPartyFirstName?.trim())
            e.AdditionalPartyFirstName = 'First name is required.';
        if (formData.AdditionalPartyType !== "MORTGAGEE" && !formData.AdditionalPartyLastName?.trim())
            e.AdditionalPartyLastName = 'Last name is required.';
        if(formData.AdditionalPartyType === "MORTGAGEE" && !formData.AdditionalPartyName?.trim())
            e.AdditionalPartyType = 'Business name is required.';
        if (!formData.AdditionalPartyAddressOne?.trim())
            e.AdditionalPartyAddressOne = 'Address is required.';
        if (!formData.AdditionalPartyCity?.trim())
            e.AdditionalPartyCity = 'City is required.';
        if (!formData.AdditionalPartyState?.trim())
            e.AdditionalPartyState = 'State is required.';
        if (!formData.AdditionalPartyZipCode?.trim())
            e.AdditionalPartyZipCode = 'ZIP code is required.';
        return e;
    };

    // -- Navigation
    const handleBack = () => history.push('/signup/confirm-details');

    const handleContinue = async () => {
        if (isReadOnly) {
            history.push('/signup/finalize');
            return; // skip validation, API calls, everything
        }
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            const firstError = document.querySelector('.field-error');
            if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        setErrors({});

        // Persist current state to DB
        if (formData.QuoteId) {
            try {
                await updateQuote(formData.QuoteId, formData);
            } catch (err) {
                console.error('Failed to update quote on continue:', err);
            }
        }

        history.push('/signup/finalize');
    };

    return (
        <SignupLayout currentStep={2} totalSteps={3} stepLabel="Confirm details">
            <h1 className="signup-heading">Add additional party names.</h1>
            <p className="signup-section__desc">
                Need to add more than one additional party?{' '}
                <a href="mailto:support@elevana.com" style={{ color: 'var(--brand-cta)' }}>
                    Contact us
                </a>{' '}
                and we'll get everyone listed on your policy.
            </p>

            {/* -- Party type -- */}
            <div className="form-group">
                <p className="signup-field-label">
                    <strong>Additional party type.</strong>
                    <span style={{ color: 'var(--error-500)', marginLeft: '2px' }}>*</span>
                </p>
                <select
                    value={formData.AdditionalPartyType || ''}
                    onChange={(e) => setField('AdditionalPartyType', e.target.value)}
                    {...readOnlyProps}
                >
                    <option value="">Select type</option>
                    {PARTY_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                </select>
                {errors.AdditionalPartyType && (
                    <span className="error-text field-error">{errors.AdditionalPartyType}</span>
                )}
            </div>

            {/* -- Party name -- */}
            <div className="form-group">
                <p className="signup-field-label">
                    <strong>{formData.AdditionalPartyType === "MORTGAGEE" ? "Business name." : "Additional party name."}</strong>
                    <span style={{ color: 'var(--error-500)', marginLeft: '2px' }}>*</span>
                </p>
                {formData.AdditionalPartyType !== "MORTGAGEE" ? (
                    <>
                        <input
                            type="text"
                            placeholder="First name"
                            value={formData.AdditionalPartyFirstName || ''}
                            onChange={(e) => setField('AdditionalPartyFirstName', e.target.value)}
                            {...readOnlyProps}
                        />
                        {errors.AdditionalPartyFirstName && (
                            <span className="error-text field-error">{errors.AdditionalPartyFirstName}</span>
                        )}
                        <input
                            type="text"
                            placeholder="Last name"
                            value={formData.AdditionalPartyLastName || ''}
                            onChange={(e) => setField('AdditionalPartyLastName', e.target.value)}
                            {...readOnlyProps}
                        />
                        {errors.AdditionalPartyLastName && (
                            <span className="error-text field-error">{errors.AdditionalPartyLastName}</span>
                        )}
                    </>
                ): (
                    <>
                        <input
                            type="text"
                            placeholder="Business name"
                            value={formData.AdditionalPartyName || ''}
                            onChange={(e) => setField('AdditionalPartyName', e.target.value)}
                            {...readOnlyProps}
                        />
                        {errors.AdditionalPartyName && (
                            <span className="error-text field-error">{errors.AdditionalPartyName}</span>
                        )}
                    </>
                )}
            </div>

            {/* -- Party mailing address -- */}
            <div className="form-group">
                <p className="signup-field-label">
                    <strong>Additional party's mailing address.</strong>
                    <span style={{ color: 'var(--error-500)', marginLeft: '2px' }}>*</span>
                </p>

                {/* Address 1 - Google Autocomplete */}
                <div className="form-group">
                    <GoogleAutoComplete
                        value={formData.AdditionalPartyAddressOne || ''}
                        onChange={handleAddress1Change}
                        onPlaceSelected={handlePlaceSelected}
                        placeholder="Address 1"
                        {...readOnlyProps}
                    />
                    {errors.AdditionalPartyAddressOne && (
                        <span className="error-text field-error">{errors.AdditionalPartyAddressOne}</span>
                    )}
                </div>

                {/* Address 2 */}
                <div className="form-group">
                    <input
                        type="text"
                        placeholder="Address 2"
                        value={formData.AdditionalPartyAddressTwo || ''}
                        onChange={(e) => setField('AdditionalPartyAddressTwo', e.target.value)}
                        {...readOnlyProps}
                    />
                </div>

                {/* City */}
                <div className="form-group">
                    <input
                        type="text"
                        placeholder="City"
                        value={formData.AdditionalPartyCity || ''}
                        onChange={(e) => setField('AdditionalPartyCity', e.target.value)}
                        {...readOnlyProps}
                    />
                    {errors.AdditionalPartyCity && (
                        <span className="error-text field-error">{errors.AdditionalPartyCity}</span>
                    )}
                </div>

                {/* State */}
                <div className="form-group">
                    <select
                        value={formData.AdditionalPartyState || ''}
                        onChange={(e) => setField('AdditionalPartyState', e.target.value)}
                        {...readOnlyProps}
                    >
                        <option value="">Select state</option>
                        {US_STATES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                    {errors.AdditionalPartyState && (
                        <span className="error-text field-error">{errors.AdditionalPartyState}</span>
                    )}
                </div>

                {/* ZIP */}
                <div className="form-group">
                    <input
                        type="text"
                        placeholder="Zip code"
                        value={formData.AdditionalPartyZipCode || ''}
                        onChange={(e) => setField('AdditionalPartyZipCode', e.target.value)}
                        maxLength={10}
                        {...readOnlyProps}
                    />
                    {errors.AdditionalPartyZipCode && (
                        <span className="error-text field-error">{errors.AdditionalPartyZipCode}</span>
                    )}
                </div>

                {/* Country */}
                <div className="form-group">
                    <select
                        value={formData.AdditionalPartyCountry || 'USA'}
                        onChange={(e) => setField('AdditionalPartyCountry', e.target.value)}
                        {...readOnlyProps}
                    >
                        {COUNTRIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* -- Navigation -- */}
            <div className="signup-nav">
                <button className="btn signup-cta btn--inverse-cta" onClick={handleBack}>
                    <MdArrowBack /> Back
                </button>
                <button className="btn signup-cta" onClick={handleContinue}>
                    Continue <MdArrowForward />
                </button>
            </div>
        </SignupLayout>
    );
}